import { useEffect, useState } from "react";
import { Trash2, ShieldCheck, UserPlus } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { IDS } from "../../constants/testIds";
import { listUsers, createUser, updateUser, deleteUser, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

export function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "dispatcher" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { listUsers().then(setUsers).catch(() => {}); }, []);

  const create = async () => {
    if (!form.email || form.password.length < 6) { toast.error("Email and a 6+ character password are required"); return; }
    setBusy(true);
    try {
      const u = await createUser(form);
      setUsers(prev => [...prev, u]);
      setForm({ email: "", name: "", password: "", role: "dispatcher" });
      toast.success(`${u.email} can now sign in`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const toggleRole = async (u) => {
    try {
      const upd = await updateUser(u.id, { role: u.role === "admin" ? "dispatcher" : "admin" });
      setUsers(prev => prev.map(x => x.id === u.id ? upd : x));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (u) => {
    try { await deleteUser(u.id); setUsers(prev => prev.filter(x => x.id !== u.id)); toast(`${u.email} removed`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid gap-3 pt-2">
      <div data-testid={IDS.users.list} className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-56 overflow-y-auto thin-scroll">
        {users.map(u => (
          <div key={u.id} data-testid={IDS.users.row(u.id)} className="flex items-center gap-3 px-3 py-2 text-sm">
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{u.name || u.email} {u.id === me?.id && <span className="text-[10px] text-slate-400">(you)</span>}</div>
              <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
            </div>
            <button data-testid={IDS.users.roleToggle(u.id)} onClick={() => toggleRole(u)} disabled={u.id === me?.id} title="Toggle admin"
              className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border ${u.role === "admin" ? "bg-slate-900 text-white border-slate-900" : "bg-slate-50 text-slate-600 border-slate-200"} disabled:opacity-60`}>
              <ShieldCheck className="h-3 w-3" /> {u.role.toUpperCase()}
            </button>
            <button data-testid={IDS.users.delete(u.id)} onClick={() => remove(u)} disabled={u.id === me?.id} className="text-slate-400 hover:text-red-600 disabled:opacity-30" aria-label="Delete user">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-dashed border-slate-300 p-3 grid gap-2">
        <div className="overline">Add dispatcher</div>
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid={IDS.users.email} type="email" placeholder="email@agency.gov" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <Input data-testid={IDS.users.name} placeholder="Display name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <Input data-testid={IDS.users.password} type="password" placeholder="Temporary password (6+)" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
          <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
            <SelectTrigger data-testid={IDS.users.role}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="dispatcher">Dispatcher</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
          </Select>
        </div>
        <Button data-testid={IDS.users.create} size="sm" className="gap-1.5 w-fit" onClick={create} disabled={busy}><UserPlus className="h-3.5 w-3.5" /> Create account</Button>
      </div>
    </div>
  );
}
